import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Loader2, XCircle, Bell, BellOff, CheckCircle2, AlertTriangle, RotateCw, ChevronDown, ChevronRight } from "lucide-react";

const POLL_MS = 3000;

/**
 * Post-upload summary card. Rendered inline in each row that has
 * `phase === "complete"`. Shows the operator exactly what landed in the
 * DB — row counts, credit breakdown, fresh/follow-up split, MySQL
 * enrichment hits — so the value delivered by this upload is immediately
 * visible without hunting through the Diagnose / Dashboard tabs.
 */
function UploadSummaryCard({ meta, testId }) {
  if (!meta) return null;
  const stats = meta.stats || {};
  const rowCount = meta.row_count || 0;
  const totalInFile = meta.total_in_file || rowCount;
  const skipped = meta.skipped_blank || 0;
  const inserted = meta.inserted || 0;
  const updated = meta.updated || 0;
  const duration = meta.duration_s || 0;
  const creditTotal = stats.credit_total ?? (
    (stats.credit_yes || 0) + (stats.credit_sms || 0) +
    (stats.credit_wn || 0) + (stats.credit_invalid || 0)
  );
  const fmt = (n) => (n == null ? "—" : Number(n).toLocaleString());
  const pctOfRows = (n) =>
    rowCount > 0 ? `${((Number(n || 0) / rowCount) * 100).toFixed(1)}%` : "—";

  return (
    <div className="mt-3 rounded-lg border border-emerald-100 bg-emerald-50/40 p-3"
         data-testid={testId}>
      <div className="text-[10px] uppercase tracking-wider font-medium text-emerald-800 mb-2">
        Upload summary
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-x-4 gap-y-2 text-xs">
        <Stat label="Rows processed" value={fmt(rowCount)}
              sub={totalInFile !== rowCount ? `of ${fmt(totalInFile)} in file` : null} />
        <Stat label="New rows" value={fmt(inserted)} sub={pctOfRows(inserted)} />
        <Stat label="Updated rows" value={fmt(updated)} sub={pctOfRows(updated)} />
        <Stat label="Duration" value={`${duration.toFixed(1)}s`}
              sub={rowCount > 0 ? `${Math.round(rowCount / Math.max(duration, 0.1))} rows/s` : null} />
        <Stat label="Fresh calls" value={fmt(stats.fresh)} sub={pctOfRows(stats.fresh)} />
        <Stat label="Follow-ups" value={fmt(stats.followup)} sub={pctOfRows(stats.followup)} />
        <Stat label="Reactivated" value={fmt(stats.reactivated)} sub={pctOfRows(stats.reactivated)}
              tone={stats.reactivated > 0 ? "info" : null} />
        <Stat label="Skipped (blank)" value={fmt(skipped)}
              tone={skipped > rowCount * 0.02 ? "warn" : null} />
      </div>
      <div className="mt-3 pt-2 border-t border-emerald-100">
        <div className="text-[10px] uppercase tracking-wider font-medium text-emerald-800 mb-1.5">
          Credit awarded on this upload
        </div>
        <div className="flex flex-wrap gap-2 text-[11px]">
          <CreditPill label="Call" value={stats.credit_yes} tone="yes" />
          <CreditPill label="SMS" value={stats.credit_sms} tone="sms" />
          <CreditPill label="WN → dep" value={stats.credit_wn} tone="wn" />
          <CreditPill label="Inv → dep" value={stats.credit_invalid} tone="wn" />
          <CreditPill label="Total credited" value={creditTotal} tone="total" />
        </div>
        <div className="text-[10px] text-slate-500 mt-1.5">
          Watch the dashboard over the next few days — late deposits get auto-credited
          up to each campaign&apos;s allowed post-call window.
        </div>
      </div>
    </div>
  );
}

function Stat({ label, value, sub, tone }) {
  const toneCls = tone === "warn"
    ? "text-amber-800"
    : tone === "info"
      ? "text-sky-800"
      : "text-slate-900";
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wide text-slate-500">{label}</div>
      <div className={`font-semibold tabular-nums ${toneCls}`}>{value}</div>
      {sub && <div className="text-[10px] text-slate-400 tabular-nums">{sub}</div>}
    </div>
  );
}

function CreditPill({ label, value, tone }) {
  const tones = {
    yes:   "bg-emerald-100 text-emerald-800 border-emerald-200",
    sms:   "bg-blue-100 text-blue-800 border-blue-200",
    wn:    "bg-amber-100 text-amber-800 border-amber-200",
    total: "bg-slate-900 text-white border-slate-900",
  };
  const n = Number(value || 0);
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full border font-semibold ${tones[tone] || tones.yes}`}>
      {label} · {n.toLocaleString()}
    </span>
  );
}

// Ordered phase list — used to draw the small stepper dots in each row.
const PHASES = [
  "queued",
  "parsing",
  "normalizing",
  "enriching_registration",
  "enriching_deposits",
  "tagging",
  "storing",
  "attribution",
  "complete",
];
const PHASE_INDEX = Object.fromEntries(PHASES.map((p, i) => [p, i]));

function pctOf(p) {
  if (!p) return 0;
  if (p.phase === "complete") return 100;
  if (p.phase === "failed" || p.phase === "cancelled") return 100;
  const idx = PHASE_INDEX[p.phase] ?? 0;
  const base = Math.round((idx / (PHASES.length - 1)) * 100);
  const rowsTotal = p.rows_total || 0;
  const rowsDone = p.rows_done || 0;
  if (rowsTotal > 0) {
    const inner = Math.round((rowsDone / rowsTotal) * (100 / (PHASES.length - 1)));
    return Math.min(100, base + inner);
  }
  return Math.max(2, base);
}

/**
 * Persistent live-progress panel for the Uploads page.
 * Polls /ops/uploads/active every 3s and shows a card per in-flight upload.
 * Fires a browser Notification when an upload transitions to done.
 *
 * Props:
 *   - onCompleted: callback fired once per upload_id when it flips to is_done
 *                  (parent can use this to refresh the completed uploads table)
 */
export default function ActiveUploadsPanel({ onCompleted }) {
  const [active, setActive] = useState([]);
  const [notifPerm, setNotifPerm] = useState(
    typeof Notification !== "undefined" ? Notification.permission : "unsupported"
  );
  // Track upload_ids we've already fired notifications for — avoids duplicates
  // when the poller sees the same "just-done" row multiple times.
  const notifiedRef = useRef(new Set());
  const pollTimer = useRef(null);
  const load = useCallback(async () => {
    try {
      const r = await api.get("/ops/uploads/active");
      const list = Array.isArray(r.data) ? r.data : [];
      setActive((prev) => {
        // Detect transitions is_done: false -> true and notify
        const prevMap = new Map(prev.map((u) => [u.upload_id, u]));
        list.forEach((u) => {
          const before = prevMap.get(u.upload_id);
          const justFinished =
            u.is_done && (!before || !before.is_done) && !notifiedRef.current.has(u.upload_id);
          if (justFinished) {
            notifiedRef.current.add(u.upload_id);
            fireCompletionSideEffects(u);
            if (typeof onCompleted === "function") onCompleted(u);
          }
        });
        return list;
      });
    } catch {
      // Silent — a network blip shouldn't spam the console. Next poll will retry.
    }
  }, [onCompleted]);

  useEffect(() => {
    load();
    pollTimer.current = setInterval(load, POLL_MS);
    return () => {
      if (pollTimer.current) clearInterval(pollTimer.current);
    };
  }, [load]);

  // Ask for notification permission ONLY when at least one upload is running
  // (avoids annoying users who never upload).
  useEffect(() => {
    if (notifPerm !== "default") return;
    const hasRunning = active.some((u) => !u.is_done);
    if (!hasRunning) return;
    // Fire-and-forget; the browser will remember the answer.
    try {
      Notification.requestPermission().then((p) => setNotifPerm(p));
    } catch {
      /* older browsers only support the callback form */
    }
  }, [active, notifPerm]);

  const enableNotifications = async () => {
    if (typeof Notification === "undefined") {
      toast.error("This browser doesn't support notifications.");
      return;
    }
    const p = await Notification.requestPermission();
    setNotifPerm(p);
    if (p === "granted") toast.success("Notifications enabled");
    else if (p === "denied") toast.info("Notifications blocked — enable them in your browser settings if you change your mind.");
  };

  const cancel = async (uploadId, hard = false) => {
    try {
      const r = await api.post(`/ops/uploads/${uploadId}/cancel`, null, {
        params: hard ? { hard: true } : undefined,
      });
      if (hard) {
        const km = r?.data?.hard;
        if (km?.ok) toast.success(`Hard-cancel sent — ${km.msg}`);
        else toast.warning(km?.msg || "Hard-cancel signalled but MySQL kill failed.");
      } else {
        toast.info("Cancel requested — will stop at next checkpoint.");
      }
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Cancel failed");
    }
  };

  const abandon = async (uploadId) => {
    try {
      await api.post(`/ops/uploads/${uploadId}/abandon`);
      toast.success("Upload force-abandoned — Mongo record marked cancelled.");
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Abandon failed");
    }
  };

  const abandonAll = async () => {
    if (!window.confirm(
      "Bulk-abandon EVERY upload currently marked as running?\n\n" +
      "This just flips their Mongo records to 'cancelled' — no server restart, " +
      "no MySQL touched. Use this to clean up zombie records left over after a backend restart."
    )) return;
    try {
      const r = await api.post("/ops/uploads/abandon-all-stuck");
      const n = r?.data?.abandoned ?? 0;
      if (n === 0) toast.info("No stuck uploads to abandon.");
      else toast.success(`${n} stuck upload${n === 1 ? "" : "s"} abandoned.`);
      load();
    } catch (ex) {
      toast.error(ex?.response?.data?.detail || "Bulk-abandon failed");
    }
  };

  if (active.filter((u) => !u.is_done).length === 0) return null;

  return (
    <div className="glass-card p-4 space-y-3" data-testid="active-uploads-panel">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div>
          <h2 className="font-medium text-slate-900">
            Live uploads · {active.filter((u) => !u.is_done).length} running
          </h2>
          <p className="text-xs text-slate-500">
            Uploads keep running on the server even if you close this tab. This panel re-attaches automatically.
          </p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {active.filter((u) => !u.is_done).length > 0 && (
            <button
              type="button"
              onClick={abandonAll}
              className="inline-flex items-center gap-1 text-[11px] text-red-700 hover:text-red-800 hover:underline"
              data-testid="active-uploads-abandon-all"
              title="Force-mark ALL running uploads as cancelled — safe cleanup for zombies"
            >
              <AlertTriangle className="w-3 h-3" /> Abandon all stuck
            </button>
          )}
          {notifPerm === "granted" ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-full px-2 py-0.5"
              data-testid="notifications-status">
              <Bell className="w-3 h-3" /> Notifications on
            </span>
          ) : notifPerm === "denied" ? (
            <span className="inline-flex items-center gap-1 text-[11px] text-slate-500 bg-slate-50 border border-slate-200 rounded-full px-2 py-0.5"
              data-testid="notifications-status">
              <BellOff className="w-3 h-3" /> Notifications blocked
            </span>
          ) : notifPerm === "unsupported" ? null : (
            <button
              type="button"
              onClick={enableNotifications}
              className="inline-flex items-center gap-1 text-[11px] text-[#0A84FF] hover:underline"
              data-testid="notifications-enable"
            >
              <Bell className="w-3 h-3" /> Enable notifications
            </button>
          )}
          <button
            type="button"
            onClick={load}
            className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900"
            data-testid="active-uploads-refresh"
            title="Refresh now"
          >
            <RotateCw className="w-3 h-3" /> Refresh
          </button>
        </div>
      </div>

      <div className="space-y-2">
        {active.filter((u) => !u.is_done).map((u) => (
          <ActiveRow key={u.upload_id} u={u} onCancel={cancel} onAbandon={abandon} />
        ))}
      </div>
    </div>
  );
}

function ActiveRow({ u, onCancel, onAbandon }) {
  const [showLogs, setShowLogs] = useState(false);
  const pct = pctOf(u);
  const isDone = !!u.is_done;
  const isFailed = u.phase === "failed";
  const isCancelled = u.phase === "cancelled";
  const isComplete = u.phase === "complete";
  const barColor = isFailed
    ? "bg-red-500"
    : isCancelled
    ? "bg-amber-500"
    : isComplete
    ? "bg-emerald-500"
    : "bg-[#0A84FF]";
  const uploader = u.ops_user_email || u.uploaded_by || "unknown";
  const startedRel = relTime(u.started_at);
  const startedAbs = formatIST(u.started_at);
  // Flag anything still "processing" but >30 min old as likely-stuck so the
  // user knows to hard-cancel or investigate the DB.
  const stale = !isDone && u.started_at
    && (Date.now() - new Date(u.started_at).getTime()) > 30 * 60 * 1000;

  return (
    <div className="glass-solid p-3 rounded-lg" data-testid={`active-upload-${u.upload_id}`}>
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-sm">
            {isComplete ? (
              <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
            ) : isFailed || isCancelled ? (
              <AlertTriangle className={`w-4 h-4 shrink-0 ${isFailed ? "text-red-600" : "text-amber-600"}`} />
            ) : (
              <Loader2 className="w-4 h-4 text-[#0A84FF] animate-spin shrink-0" />
            )}
            <span className="font-medium text-slate-900 truncate" title={u.filename}>
              {u.filename || u.upload_id.slice(0, 8)}
            </span>
            {u.report_type && (
              <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded-full bg-blue-100 text-blue-800 shrink-0">
                {u.report_type}
              </span>
            )}
          </div>
          <div className="text-[11px] text-slate-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5"
               data-testid={`active-upload-meta-${u.upload_id}`}>
            <span title={startedAbs || ""}>Started {startedRel || "—"}</span>
            <span>by <span className="text-slate-700 font-medium">{uploader}</span></span>
            {u.file_size_kb ? <span>· {formatBytes(u.file_size_kb * 1024)}</span> : null}
            {stale && (
              <span className="inline-flex items-center gap-1 px-1.5 py-0 rounded-full bg-amber-100 text-amber-800 border border-amber-200 text-[10px] font-medium"
                title="Running longer than 30 minutes — likely stuck. Consider hard-cancel or KILL QUERY in MySQL.">
                <AlertTriangle className="w-2.5 h-2.5" /> stale
              </span>
            )}
          </div>
          <div className="text-xs text-slate-600 mt-1 truncate" data-testid={`active-upload-phase-${u.upload_id}`}>
            {u.phase_label || u.phase}
            {u.rows_total > 0 && (
              <>
                {" — "}
                <span className="font-mono">{(u.rows_done || 0).toLocaleString()} / {u.rows_total.toLocaleString()} rows</span>
              </>
            )}
          </div>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <Link
            to={`/ops/uploads/${u.upload_id}`}
            className="text-[11px] text-[#0A84FF] hover:underline"
            data-testid={`active-upload-open-${u.upload_id}`}
          >
            Open
          </Link>
          {!isDone && (
            <>
              <button
                type="button"
                onClick={() => onCancel(u.upload_id, false)}
                className="inline-flex items-center gap-1 text-[11px] text-slate-600 hover:text-slate-900"
                data-testid={`active-upload-cancel-${u.upload_id}`}
                title="Ask the pipeline to stop at the next chunk boundary"
              >
                <XCircle className="w-3 h-3" /> Cancel
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(
                    "Hard-cancel sends KILL QUERY to MySQL, interrupting the current query immediately. " +
                    "The upload will be marked as failed. Continue?"
                  )) onCancel(u.upload_id, true);
                }}
                className="inline-flex items-center gap-1 text-[11px] text-amber-600 hover:text-amber-700 font-medium"
                data-testid={`active-upload-hard-cancel-${u.upload_id}`}
                title="Immediate: sends KILL QUERY to MySQL to interrupt the blocking query"
              >
                <XCircle className="w-3 h-3" /> Force-kill
              </button>
              <button
                type="button"
                onClick={() => {
                  if (window.confirm(
                    "Force-abandon flips this upload to 'cancelled' in the database WITHOUT touching the worker " +
                    "or MySQL. Use this when the worker is already dead (e.g. after a backend restart) but the " +
                    "upload still shows as running. Continue?"
                  )) onAbandon(u.upload_id);
                }}
                className="inline-flex items-center gap-1 text-[11px] text-red-600 hover:text-red-700 font-medium"
                data-testid={`active-upload-abandon-${u.upload_id}`}
                title="Force-mark as cancelled in Mongo — use when Force-kill can't reach the worker"
              >
                <AlertTriangle className="w-3 h-3" /> Abandon
              </button>
            </>
          )}
        </div>
      </div>
      <div className="mt-2 h-1.5 bg-slate-100 rounded-full overflow-hidden">
        <div
          className={`h-full ${barColor} transition-all`}
          style={{ width: `${pct}%` }}
          data-testid={`active-upload-bar-${u.upload_id}`}
        />
      </div>
      {isComplete && u.meta && (
        <UploadSummaryCard meta={u.meta} testId={`upload-summary-${u.upload_id}`} />
      )}
      {isFailed && u.error && (
        <div className="mt-1 text-[10px] text-red-700 font-mono truncate" title={u.error}>
          {u.error}
        </div>
      )}
      {/* Live logs — collapsed by default; expand to see per-chunk MySQL telemetry. */}
      <button
        type="button"
        onClick={() => setShowLogs((v) => !v)}
        className="mt-2 inline-flex items-center gap-1 text-[10px] text-slate-500 hover:text-slate-800"
        data-testid={`active-upload-toggle-logs-${u.upload_id}`}
      >
        {showLogs ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
        {showLogs ? "Hide" : "Show"} live logs
        {Array.isArray(u.log_lines) && u.log_lines.length > 0 && (
          <span className="text-slate-400">· {u.log_lines.length}</span>
        )}
      </button>
      {showLogs && (
        <div className="mt-1 bg-slate-950 text-slate-200 rounded-md p-2 max-h-48 overflow-y-auto font-mono text-[10px] leading-relaxed"
             data-testid={`active-upload-logs-${u.upload_id}`}>
          {Array.isArray(u.log_lines) && u.log_lines.length > 0 ? (
            u.log_lines.map((ln, i) => (
              <div key={i} className={
                ln.level === "error" ? "text-red-300"
                : ln.level === "warn" ? "text-amber-300"
                : ln.level === "debug" ? "text-slate-400"
                : "text-slate-100"
              }>
                <span className="text-slate-500">
                  {new Date(ln.ts).toLocaleTimeString("en-IN", { hour12: false })}
                </span>{" "}
                {ln.msg}
              </div>
            ))
          ) : (
            <div className="text-slate-400 italic">No log lines yet — logs stream in as the pipeline runs.</div>
          )}
        </div>
      )}
    </div>
  );
}

// --- Small formatting helpers ---
// "12 min ago", "3 h ago", "2 d ago". Falls back to abs date for > 7 days.
function relTime(iso) {
  if (!iso) return "";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "";
  const diff = Math.floor((Date.now() - t) / 1000);
  if (diff < 30) return "just now";
  if (diff < 60) return `${diff}s ago`;
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 7 * 86400) return `${Math.floor(diff / 86400)}d ago`;
  return formatIST(iso);
}

// IST-formatted absolute date for tooltip / fallback.
function formatIST(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      timeZone: "Asia/Kolkata",
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit", hour12: false,
    }) + " IST";
  } catch {
    return iso;
  }
}

function formatBytes(bytes) {
  if (!bytes || bytes < 1024) return `${bytes || 0} B`;
  const kb = bytes / 1024;
  if (kb < 1024) return `${kb.toFixed(0)} KB`;
  return `${(kb / 1024).toFixed(1)} MB`;
}

// --- Side-effects when an upload flips to done ---------------------------
function fireCompletionSideEffects(u) {
  const filename = u.filename || u.upload_id.slice(0, 8);
  const meta = u.meta || {};
  const stats = meta.stats || {};

  if (u.phase === "complete") {
    const desc = meta.row_count
      ? `${meta.row_count} rows saved (${meta.inserted || 0} new, ${meta.updated || 0} updated).`
      : "Ingestion pipeline finished.";
    toast.success(`Upload complete — ${filename}`, { description: desc, duration: 8000 });
    tryNotify(`Upload complete — ${filename}`, {
      body: `${desc}${stats.credit_yes ? ` Credit-yes: ${stats.credit_yes}.` : ""}`,
      tag: `ops-upload-${u.upload_id}`,
    });
  } else if (u.phase === "failed") {
    const err = u.error || u.phase_label || "Upload failed";
    toast.error(`Upload failed — ${filename}`, { description: err, duration: 12000 });
    tryNotify(`Upload failed — ${filename}`, { body: err, tag: `ops-upload-${u.upload_id}` });
  } else if (u.phase === "cancelled") {
    toast.info(`Upload cancelled — ${filename}`, { duration: 6000 });
    tryNotify(`Upload cancelled — ${filename}`, { body: "You cancelled this upload.", tag: `ops-upload-${u.upload_id}` });
  }
}

function tryNotify(title, opts) {
  if (typeof Notification === "undefined") return;
  if (Notification.permission !== "granted") return;
  try {
    const n = new Notification(title, {
      body: opts?.body || "",
      tag: opts?.tag,
      // Reuse the same tag to auto-collapse a duplicate for the same upload.
      renotify: false,
    });
    // Focus this tab if the user clicks the notification.
    n.onclick = () => {
      try {
        window.focus();
        n.close();
      } catch {
        /* noop */
      }
    };
  } catch {
    /* Some browsers throw if the tab isn't allowed to spawn — swallow. */
  }
}
